/** Shopify product events use the JWT intake, never the support SDK. Attempts retain their original view. */
import { createUiTracker } from '@testfeed/observability/ui-tracker'
import type { UiFailure } from '@testfeed/observability/ui-events'
import { reportClientUiEvent } from './clientDiagnostics'
import { isAbortError, pageErrorFrom } from './page-state'
import { captureShopifyDiagnosticScope, type ShopifyAppBridge, type ShopifyDiagnosticScope } from './shopifyApiFetch'
import { shopifyUiPages, type ShopifyUiActionId } from './shopifyTrackingCatalog'

export type { ShopifyUiActionId } from './shopifyTrackingCatalog'

type Tracker = ReturnType<typeof createUiTracker<ShopifyUiActionId>>
const trackers = new WeakMap<ShopifyAppBridge, Tracker>()

export function shopifyUiTracker(shopify: ShopifyAppBridge): Tracker {
  let tracker = trackers.get(shopify)
  if (!tracker) {
    const scopes = new Map<string, ShopifyDiagnosticScope>()
    tracker = createUiTracker<ShopifyUiActionId>({
      productSurface: 'shopify',
      pages: shopifyUiPages,
      send: (event) => {
        if (event.event === 'screen_viewed') scopes.set(event.viewId, captureShopifyDiagnosticScope(shopify))
        const scope = scopes.get(event.viewId)
        if (!scope?.sessionIdentity) {
          console.error('UI_EVENT_VERIFIED_SCOPE_UNAVAILABLE')
          return
        }
        reportClientUiEvent(shopify, event, scope)
      },
    })
    trackers.set(shopify, tracker)
  }
  return tracker
}

/** Safe machine classifications only; thrown text and arbitrary error codes never become analytics properties. */
export function shopifyUiFailure(cause: unknown): UiFailure {
  const error = pageErrorFrom(cause)
  const status = typeof cause === 'object' && cause !== null && 'status' in cause ? cause.status : undefined
  const httpStatus =
    typeof status === 'number' && Number.isInteger(status) && status >= 100 && status <= 599 ? status : undefined
  let errorCode: UiFailure['errorCode'] = 'UNEXPECTED_FAILURE'
  if (httpStatus) errorCode = 'REQUEST_FAILED'
  if (error.name === 'network') errorCode = 'NETWORK_FAILED'
  if (error.name === 'invalid-data' || error.name === 'unsupported-version') errorCode = 'VALIDATION_REFUSED'
  if (error.name === 'not-allowed') errorCode = 'AUTH_REJECTED'
  return { errorCode, ...(httpStatus ? { httpStatus } : {}) }
}

/** Existing synchronous links/local handlers stay synchronous; no exclusive mutation or navigation policy is added. */
export function trackShopifyAction<T>(shopify: ShopifyAppBridge, action: ShopifyUiActionId, work: () => T): T {
  const tracker = shopifyUiTracker(shopify)
  const attempt = tracker.request(action)
  const failed = (cause: unknown) => {
    if (isAbortError(cause)) tracker.cancelled(attempt, 'aborted')
    else tracker.failed(attempt, shopifyUiFailure(cause))
  }
  try {
    const result = work()
    if (result instanceof Promise) {
      // Attach bookkeeping without replacing the promise or changing its rejection behavior.
      void result.then(() => tracker.completed(attempt), failed)
    } else tracker.completed(attempt)
    return result
  } catch (cause) {
    failed(cause)
    throw cause
  }
}

/** Query refetch resolves an error result rather than rejecting by default; do not call that completion. */
export function trackShopifyRefetch<T extends { isError: boolean; error: unknown }>(
  shopify: ShopifyAppBridge,
  work: () => Promise<T>,
): Promise<T> {
  const tracker = shopifyUiTracker(shopify)
  const attempt = tracker.request('retry-load')
  return work().then(
    (result) => {
      if (result.isError) tracker.failed(attempt, shopifyUiFailure(result.error))
      else tracker.completed(attempt)
      return result
    },
    (cause: unknown) => {
      if (isAbortError(cause)) tracker.cancelled(attempt, 'aborted')
      else tracker.failed(attempt, shopifyUiFailure(cause))
      throw cause
    },
  )
}
